import { AsyncPipe, JsonPipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, OnInit, viewChild, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { PaginatorModule } from 'primeng/paginator';
import { SharedModule } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { ToolService } from '../../../service/tool.service';
import { TransactionService } from '../../../service/transaction.service';
import { CantaaMessageService } from '../../../service/cantaa-message.service';
import { CantaaErrorHandlerService } from '../../../service/cantaa-error-handler.service';
import { ToolDocumentService } from '../../../service/tool-document.service';
import { InspectionBookingDto } from '../../../model/booking.models';
import { FocusHelper } from '../../../service/helper/focus-helper';
import { ToolDetailItem } from '../../../model/tool-detail.model';
import { InspectionService } from '../../../service/inspection.service';
import { ToolInspectionListItem } from '../../../model/tool-inspection-list-item.model';
import { TranslatedValue } from '../../../model/inspection-result.model';
import { TranslateModule } from '@ngx-translate/core';
import { DebugComponent } from '../../debug/debug.component';
import { Router } from '@angular/router';
import { TestDataComponent } from '../../test-data/test-data.component';
import { SuggestionInputComponent } from '../../inputs/suggestion-input/suggestion-input.component';
import { ToolListItem } from '../../../model/tool-list-item.model';
import { DisplayHelper } from '../../../service/helper/DisplayHelper';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MAT_DATE_LOCALE} from "@angular/material/core";
import {DocumentService} from "../../../service/document.service";
import {ToolLocationService} from "../../../service/tool-location.service";
import {DropdownItemDto} from "../../../model/dropdown-item.model";
import {Observable, map, startWith} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'wim-tool-inspection-booking',
  standalone: true,
  templateUrl: './tool-inspection-booking.component.html',
  styleUrl: './tool-inspection-booking.component.scss',
  imports: [
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatIcon,
    MatOption,
    MatSelect,
    MatAutocompleteModule,
    PaginatorModule,
    ReactiveFormsModule,
    SharedModule,
    TableModule,
    JsonPipe,
    TranslateModule,
    DebugComponent,
    TestDataComponent,
    SuggestionInputComponent,
    MatDatepickerInput,
    MatDatepicker,
    MatDatepickerToggle,
    MatSuffix,
    AsyncPipe,
  ],
  providers: [
    // 👇 Nutzt dynamisch die aktuelle Sprache des Browsers
    { provide: MAT_DATE_LOCALE, useValue: navigator.language }
  ]
})
export class ToolInspectionBookingComponent implements OnInit {
  protected readonly DisplayHelper = DisplayHelper;
  private destroyRef = inject(DestroyRef);
  selectedTool: ToolDetailItem | null = null;
  inspections: ToolInspectionListItem[] = [];
  inspectorsForDropdown: DropdownItemDto[] = [];
  filteredInspectorsForDropdown$?: Observable<DropdownItemDto[]>;
  inspectionLocationSearchControl = this.fb.control<string | DropdownItemDto>('');
  receivedToolId: number | null = null;
  toolSuggestionInput = viewChild.required<SuggestionInputComponent<ToolListItem>>('toolSuggestionInput');
  @ViewChild(MatAutocompleteTrigger) inspectionLocationAutocompleteTrigger?: MatAutocompleteTrigger;

  results: TranslatedValue[] = [
    { value: 'SUCCESS', translateKey: 'RESULT.SUCCESS' },
    { value: 'FAILURE', translateKey: 'RESULT.FAILURE' }
  ];

  inspectionForm = this.fb.group({
    inspection: this.fb.control<ToolInspectionListItem | null>({ value: null, disabled: true }, Validators.required),
    actualInspectionDate: this.fb.control<Date | null>({ value: new Date(), disabled: false }, Validators.required),
    inspectionLocationId: this.fb.control<DropdownItemDto | null>({ value: null, disabled: false }, Validators.required),
    result: this.fb.control<TranslatedValue | null>({ value: null, disabled: false }, Validators.required),
    comment: this.fb.control<string | null>({ value: null, disabled: false }),
  });

  constructor(private fb: FormBuilder,
              private toolService: ToolService,
              private transactionService: TransactionService,
              private messageService: CantaaMessageService,
              private el: ElementRef,
              private errorHandler: CantaaErrorHandlerService,
              protected toolDocumentService: ToolDocumentService,
              private documentService: DocumentService,
              private inspectionService: InspectionService,
              private toolLocationService: ToolLocationService,
              private router: Router) {
    const receivedToolIdString = this.router.getCurrentNavigation()?.extras?.state?.['id'];
    this.receivedToolId = receivedToolIdString ? +receivedToolIdString : null;
  }

  async ngOnInit() {
    this.toolSuggestionInput().focus();

    this.inspectorsForDropdown = await this.fetchInspectorsForDropdown();
    this.initInspectorFilter();

    await this.handleReceivedRfid();
  }

  private async handleReceivedRfid() {
    if (!this.receivedToolId) {
      return;
    }

    const toolDetail = await this.fetchToolDetail(this.receivedToolId);

    const listItem = {
      id: toolDetail.id,
      category: toolDetail.category,
      rfid: toolDetail.rfid
    } as ToolListItem;

    this.toolSuggestionInput().setValue(listItem);

    await this.onToolSelected(listItem, toolDetail);
  }

  private async fetchToolDetail(toolId: number) {
    try {
      return await this.toolService.getToolDetail(toolId);
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      throw e;
    }
  }

  async onToolSelected(toolListItem: ToolListItem | null, toolDetailItem: ToolDetailItem | null = null) {

    if (!toolListItem) {
      this.selectedTool = null;
      return;
    }

    const currentDateValue = this.inspectionForm.controls.actualInspectionDate.value;
    const currentInspectionLocationId = this.inspectionForm.controls.inspectionLocationId.value;
    const currentResult = this.inspectionForm.controls.result.value;
    const currentComment = this.inspectionForm.controls.comment.value;

    this.inspectionForm.reset({
      actualInspectionDate: currentDateValue,
      inspectionLocationId: currentInspectionLocationId,
      result: currentResult,
      comment: currentComment,
    });

    this.syncInspectionLocationSearchControl();

    if (toolDetailItem) {
      this.selectedTool = toolDetailItem;
    } else {
      this.selectedTool = await this.toolService.getToolDetail(toolListItem.id);
    }

    this.messageService.success('TOOL.FOUND');

    this.toolDocumentService.getDefaultPhotoScaled70x70_2ImgEleId(this.selectedTool.id, 'inspection_tool_icon');

    this.inspections = await this.fetchToolInspectionsByToolId(this.selectedTool.id);

    if (this.inspections.length == 0) {
      this.messageService.error('INSPECTION.NOT_FOUND');
      return;
    }

    this.inspectionForm.controls.inspection.enable();

    if (this.inspections.length > 1) {
      this.focusToInspection();
      return;
    }

    this.inspectionForm.patchValue({
      inspection: this.inspections[0]
    })

    this.syncInspectionLocationSearchControl();
  }

  private async fetchToolInspectionsByToolId(id: number) {
    try {
      return await this.inspectionService.getToolInspectionsByTool(id, false);
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      throw e;
    }
  }

  private async fetchInspectorsForDropdown() {
    try {
      return await this.toolLocationService.getInspectorsForDropdown()
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      throw e;
    }
  }

  onInspectionChange() {
    this.syncInspectionLocationSearchControl();
  }

  onResultChange() {
    this.focusToComment();
  }

  focusToInspection() {
    FocusHelper.focusFormField(this.el, 'inspection');
  }

  focusToResult() {
    FocusHelper.focusFormField(this.el, 'result');
  }

  focusToComment() {
    FocusHelper.focusFormField(this.el, 'comment');
  }

  onCancel() {
    this.initUi();
  }

  private initUi() {
    this.selectedTool = null;
    this.toolSuggestionInput().clear();
    const currentDateValue = this.inspectionForm.controls.actualInspectionDate.value;
    const currentInspectionLocationId = this.inspectionForm.controls.inspectionLocationId.value;
    const currentResult = this.inspectionForm.controls.result.value;
    const currentComment = this.inspectionForm.controls.comment.value;

    this.inspectionForm.reset({
      actualInspectionDate: currentDateValue,
      inspectionLocationId: currentInspectionLocationId,
      result: currentResult,
      comment: currentComment,
    });

    this.syncInspectionLocationSearchControl();
    this.documentService.disableImgElement('inspection_tool_icon');
  }

  //Save
  async onBooking() {
    const booking = {
      transactionTypeCode: 'Inspection',
      toolId: this.selectedTool?.id,
      toolInspectionId: this.inspectionForm.controls.inspection.value?.id,
      actualInspectionDate: this.inspectionForm.controls.actualInspectionDate.getRawValue(),
      inspectionlocationid: this.inspectionForm.controls.inspectionLocationId.value?.id,
      result: this.inspectionForm.controls.result.value?.value,
      comment: this.inspectionForm.controls.comment.value
    } as InspectionBookingDto

    await this.makeBooking(booking);

    this.messageService.success('BOOKING.SUCCESSFUL');
    this.initUi();
  }

  private async makeBooking(booking: InspectionBookingDto) {
    try {
      await this.transactionService.makeInspectionBooking(booking)
    } catch (e) {
      this.errorHandler.handleError(e, 'BOOKING.FAILED');
      throw e;
    }
  }

  toolFetchSuggestions = async (query: string): Promise<ToolListItem[]> => {
    return await this.toolService.searchTools(query);
  };

  onInspectionLocationSelected(event: MatAutocompleteSelectedEvent) {
    const inspector = event.option.value as DropdownItemDto;
    this.inspectionForm.controls.inspectionLocationId.setValue(inspector);
    this.inspectionLocationSearchControl.setValue(inspector);
  }

  displayInspectionLocation = (value: DropdownItemDto | string | null): string => {
    if (!value || typeof value === 'string') {
      return typeof value === 'string' ? value : '';
    }

    return value.name;
  };

  onInspectionLocationFocus() {
    this.inspectionLocationAutocompleteTrigger?.openPanel();
  }

  private syncInspectionLocationSearchControl() {
    this.inspectionLocationSearchControl.setValue(
      this.inspectionForm.controls.inspectionLocationId.value,
      { emitEvent: false }
    );
  }

  private initInspectorFilter() {
    this.inspectionLocationSearchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (typeof value === 'string') {
          this.inspectionForm.controls.inspectionLocationId.setValue(null, { emitEvent: false });
        } else {
          this.inspectionForm.controls.inspectionLocationId.setValue(value, { emitEvent: false });
        }
      });

    this.filteredInspectorsForDropdown$ = this.inspectionLocationSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const query = typeof value === 'string'
          ? value.toLowerCase()
          : value?.name.toLowerCase() ?? '';

        if (!query) {
          return this.inspectorsForDropdown;
        }

        return this.inspectorsForDropdown.filter(inspector =>
          inspector.name.toLowerCase().includes(query)
        );
      })
    );
  }

}
